import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import Index from "./pages/Index";
import ProtectedRoute from "./components/ProtectedRoute";
import PasswordProtectionWrapper from "./components/PasswordProtectionWrapper";
import MaintenanceWrapper from "./components/MaintenanceWrapper";
import PasswordProtection from "./pages/PasswordProtection";
import { DebugToggle } from "@/components/DebugToggle";

// Lazy load non-critical pages
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const CGU = lazy(() => import("./pages/CGU"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Profile = lazy(() => import("./pages/Profile"));
const MagicLinkConfirmation = lazy(() => import("./pages/MagicLinkConfirmation"));
const EmailSent = lazy(() => import("./pages/EmailSent"));

// Lazy load admin pages
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const Users = lazy(() => import("./pages/admin/Users"));
const TwoFactor = lazy(() => import("./pages/admin/TwoFactor"));
const Migrate2FA = lazy(() => import("./pages/admin/Migrate2FA"));
const SiteProtection = lazy(() => import("./pages/admin/SiteProtection"));
const SitePasswordLogs = lazy(() => import("./pages/admin/SitePasswordLogs"));
const IPControl = lazy(() => import("./pages/admin/IPControl"));
const Maintenance = lazy(() => import("./pages/admin/Maintenance"));
const SearchConfiguration = lazy(() => import("./pages/admin/SearchConfiguration"));
const DemoProducts = lazy(() => import("./pages/admin/DemoProducts"));
const SearchExport = lazy(() => import("./pages/admin/SearchExport"));
const ScraperMonitoring = lazy(() => import("./pages/admin/ScraperMonitoring"));
const RateLimiting = lazy(() => import("./pages/admin/RateLimiting"));
const AuditTrail = lazy(() => import("./pages/admin/AuditTrail"));
const ConsentLogs = lazy(() => import("./pages/admin/ConsentLogs"));
const PriceAnalytics = lazy(() => import("./pages/admin/PriceAnalytics"));
const CacheManagement = lazy(() => import("./pages/admin/CacheManagement"));
const BlogManagement = lazy(() => import("./pages/admin/BlogManagement"));

const queryClient = new QueryClient();

const App = () => (
  <>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <PasswordProtectionWrapper>
            <MaintenanceWrapper>
              <Suspense fallback={<div className="min-h-screen" />}>
                <Routes>
                  <Route path="/password-protection" element={<PasswordProtection />} />
                  <Route path="/" element={<Index />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogArticle />} />
                  <Route path="/cgu" element={<CGU />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/magic-link-confirmation" element={<MagicLinkConfirmation />} />
                  <Route path="/email-sent" element={<EmailSent />} />
                  
                  {/* Client Dashboard */}
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <Profile />
                      </ProtectedRoute>
                    }
                  />
                
                {/* Admin Routes */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute requireDashboardAccess>
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <ProtectedRoute requireDashboardAccess>
                      <Users />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/2fa"
                  element={
                    <ProtectedRoute requireDashboardAccess>
                      <TwoFactor />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/migrate-2fa"
                  element={
                    <ProtectedRoute requireDashboardAccess>
                      <Migrate2FA />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/site-protection"
                  element={
                    <ProtectedRoute requireDashboardAccess>
                      <SiteProtection />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/site-password-logs"
                  element={
                    <ProtectedRoute requireDashboardAccess>
                      <SitePasswordLogs />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/ip-control"
                  element={
                    <ProtectedRoute requireDashboardAccess>
                      <IPControl />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/maintenance"
                  element={
                    <ProtectedRoute requireDashboardAccess>
                      <Maintenance />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/search-configuration"
                  element={
                    <ProtectedRoute requireDashboardAccess>
                      <SearchConfiguration />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/demo-products"
                  element={
                    <ProtectedRoute requireDashboardAccess>
                      <DemoProducts />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/search-export"
                  element={
                    <ProtectedRoute requireDashboardAccess>
                      <SearchExport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/scraper-monitoring"
                  element={
                    <ProtectedRoute requireDashboardAccess>
                      <ScraperMonitoring />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/rate-limiting"
                  element={
                    <ProtectedRoute requireDashboardAccess>
                      <RateLimiting />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/audit-trail"
                  element={
                    <ProtectedRoute requireDashboardAccess>
                      <AuditTrail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/consent-logs"
                  element={
                    <ProtectedRoute requireDashboardAccess>
                      <ConsentLogs />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/price-analytics"
                  element={
                    <ProtectedRoute requireDashboardAccess>
                      <PriceAnalytics />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/cache-management"
                  element={
                    <ProtectedRoute requireDashboardAccess>
                      <CacheManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/blog"
                  element={
                    <ProtectedRoute requireDashboardAccess>
                      <BlogManagement />
                    </ProtectedRoute>
                  }
                />
                
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
            </MaintenanceWrapper>
          </PasswordProtectionWrapper>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
    <DebugToggle />
  </>
);

export default App;
